import { useState } from 'preact/hooks';
import type { PageProps } from '../app';
import { BUILTIN_SOURCES } from '../core/loglist/sources';
import { proxyFetcher } from '../core/transport/fetcher';
import { Badge } from '../ui/components/Badge';
import { Icon } from '../ui/components/Icon';
import { Card } from '../ui/components/Section';
import { forgetClients, tileCache } from '../ui/state/clients';
import { ensureRegistry } from '../ui/state/logs';
import { builtinProxyAvailable } from '../ui/state/proxy';
import { settings, updateSettings, type ProxyMode, type Settings } from '../ui/state/settings';
import { storage } from '../ui/state/storage';
import { activeProxyUrl, reachability } from '../ui/state/transport';
import { href } from '../router';
import { Breadcrumbs } from '../ui/components/Breadcrumbs';
import { ReturnStrip } from '../ui/components/ReturnStrip';
import { Segmented } from '../ui/components/Segmented';
import { isDesktop } from '../ui/state/desktop';

const TEST_URL = 'https://ct.cloudflare.com/logs/nimbus2026/ct/v1/get-sth'; // a log that never sends CORS headers

export function SettingsPage({ query }: PageProps) {
  const s = settings.value;
  const desktop = isDesktop();
  // Off merklens.app the built-in proxy refuses the page's origin, so only "own proxy" and "off" apply there.
  const builtinAvailable = builtinProxyAvailable();
  const [proxyTest, setProxyTest] = useState<{ status: 'idle' | 'running' | 'ok' | 'fail'; detail?: string }>({ status: 'idle' });
  const [customList, setCustomList] = useState(s.customListUrl);
  const [cleared, setCleared] = useState<string>();

  const testProxy = async () => {
    const base = activeProxyUrl.value;
    if (!base) return;
    setProxyTest({ status: 'running' });
    try {
      const res = await proxyFetcher(base).fetch(TEST_URL);
      if (res.status !== 200) throw new Error(`proxy answered HTTP ${res.status}`);
      const json = JSON.parse(new TextDecoder().decode(res.body)) as { tree_size?: number };
      if (typeof json.tree_size !== 'number') throw new Error('response is not a get-sth JSON');
      setProxyTest({ status: 'ok', detail: `Fetched Cloudflare Nimbus' STH through the proxy (tree size ${json.tree_size.toLocaleString('en-US')}).` });
    } catch (e) {
      setProxyTest({ status: 'fail', detail: e instanceof Error ? e.message : String(e) });
    }
  };
  const toggleSource = (id: string) => {
    const cur = new Set(s.sources);
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    updateSettings({ sources: [...cur] });
    void ensureRegistry(true);
  };
  const applyCustomList = () => {
    updateSettings({ customListUrl: customList.trim() });
    void ensureRegistry(true);
  };
  const clearAll = async () => {
    reachability.clear();
    await tileCache.clear();
    forgetClients();
    storage.removeItem('ct:pageSize:v1');
    try {
      const ls = globalThis.localStorage;
      for (let i = ls.length - 1; i >= 0; i--) {
        const k = ls.key(i);
        if (k && (k.startsWith('ct:heads:') || k.startsWith('ct:loglist:'))) ls.removeItem(k);
      }
    } catch { /* no localStorage */ }
    setCleared('Cleared reachability, tiles, tree-head history, log list and page sizes.');
  };

  return (
    <main class="content page-narrow">
      <Breadcrumbs items={[{ label: 'Home', href: href('/') }, { label: 'Settings' }]} />
      <div class="page-head">
        <div>
          <h1>Settings</h1>
          <p class="lede">Everything is stored in this browser only.</p>
        </div>
      </div>
      <ReturnStrip query={query} note={desktop ? undefined : 'a proxy chosen here is used right away when you go back'} />
      <div class="stack">
        {desktop && (
          <Card title={<><Icon name="link" /> Desktop app</>}>
            <p class="small muted" style="margin:0">Requests go straight from this app to the logs through the operating system's HTTP stack, so browser CORS rules do not apply and no proxy is needed. Every log in the lists is reachable as long as the log itself is up.</p>
          </Card>
        )}
        {!desktop && <Card title={<><Icon name="link" /> Reaching logs that block browsers</>} foot={<>The built-in proxy and the one you can run yourself are the same ~150-line Cloudflare Worker: <a href="https://gitlab.com/merklens/proxy" target="_blank" rel="noreferrer">gitlab.com/merklens/proxy</a>. The desktop app needs no proxy.</>}>
          <p class="small muted">
            Many logs do not send <code>Access-Control-Allow-Origin</code>, so a web page cannot read them. A proxy is used only for requests the browser itself was refused; everything else goes straight to the log. Every response is still verified here (signatures, Merkle proofs), so a proxy cannot forge data unnoticed — it only sees which log URLs you fetch.
          </p>
          <div class="stack" style="gap:0.5rem">
            {(
              [
                ['ask', 'Ask me when a log blocks the browser', 'The default: the built-in proxy is offered right where a request was blocked.'],
                ['builtin', 'Use the built-in proxy', 'Run by Merkle Lens for this site; forwards only to known CT logs.'],
                ['custom', 'Use my own proxy', 'Any CORS proxy you run; the URL stays in this browser.'],
                ['off', 'Never use a proxy', 'Blocked logs stay blocked; use the desktop app for those.'],
              ] as [ProxyMode, string, string][]
            )
              .filter(([mode]) => builtinAvailable || (mode !== 'builtin' && mode !== 'ask'))
              .map(([mode, label, description]) => (
                <label key={mode} class="check" style="align-items:flex-start">
                  <input type="radio" name="proxy" value={mode} checked={s.proxy === mode || (mode === 'off' && s.proxy === 'ask' && !builtinAvailable)} onChange={() => updateSettings({ proxy: mode })} style="margin-top:0.25rem" />
                  <span><strong>{label}</strong><div class="tiny muted">{description}</div></span>
                </label>
              ))}
          </div>
          {s.proxy === 'custom' && (
            <label class="field" style="margin-top:0.6rem">
              <span>Proxy URL <span class="hint">the target is appended as <code>?url=…</code>, or put <code>{'{url}'}</code> where it should go</span></span>
              <input class="input mono" value={s.proxyUrl} onInput={(e) => updateSettings({ proxyUrl: (e.target as HTMLInputElement).value })} placeholder="https://my-ct-proxy.example.workers.dev/" />
            </label>
          )}
          {(s.proxy === 'builtin' || s.proxy === 'custom') && (
            <div class="form-actions">
              <button type="button" class="btn" onClick={testProxy} disabled={!activeProxyUrl.value || proxyTest.status === 'running'}><Icon name="play" /> {proxyTest.status === 'running' ? 'Testing…' : 'Test proxy'}</button>
              {proxyTest.status === 'ok' && <Badge tone="ok">works</Badge>}
              {proxyTest.status === 'fail' && <Badge tone="err">failed</Badge>}
              {proxyTest.detail && <span class="small muted">{proxyTest.detail}</span>}
            </div>
          )}
        </Card>}

        <Card title={<><Icon name="list" /> Log lists</>} foot={<>Logs from all enabled lists are merged by log ID; each program's state is shown separately. Firefox uses Chrome's list, so it has no list of its own.</>}>
          <div class="stack" style="gap:0.6rem">
            {BUILTIN_SOURCES.map((src) => (
              <label key={src.id} class="check" style="align-items:flex-start">
                <input type="checkbox" checked={s.sources.includes(src.id)} onChange={() => toggleSource(src.id)} style="margin-top:0.25rem" />
                <span><strong>{src.label}</strong><div class="tiny muted">{src.description} — <code>{src.url}</code></div></span>
              </label>
            ))}
            <label class="field" style="margin-top:0.4rem">
              <span>Additional list URL <span class="hint">log_list_v3 schema; must send CORS headers</span></span>
              <div class="row">
                <input class="input input-sm mono grow" style="max-width:520px" value={customList} onInput={(e) => setCustomList((e.target as HTMLInputElement).value)} placeholder="https://…/log_list.json" />
                <button type="button" class="btn btn-sm" disabled={customList.trim() === s.customListUrl} onClick={applyCustomList}>{customList.trim() ? 'Use' : 'Remove'}</button>
              </div>
            </label>
            <label class="check" style="margin-top:0.4rem"><input type="checkbox" checked={s.showRetired} onChange={(e) => updateSettings({ showRetired: (e.target as HTMLInputElement).checked })} /> show retired and rejected logs everywhere</label>
          </div>
        </Card>

        <Card title={<><Icon name="sun" /> Appearance</>}>
          <Segmented<Settings['theme']> name="theme" value={s.theme} onChange={(t) => updateSettings({ theme: t })} label="Theme" options={[{ value: 'auto', label: 'Follow system', icon: 'settings' }, { value: 'light', label: 'Light', icon: 'sun' }, { value: 'dark', label: 'Dark', icon: 'moon' }]} />
        </Card>

        <Card title={<><Icon name="refresh" /> Local data</>}>
          <div class="row">
            <button type="button" class="btn" onClick={() => void clearAll()}>Clear caches</button>
            {cleared && <span class="small muted">{cleared}</span>}
          </div>
        </Card>
      </div>
    </main>
  );
}
